import { RowSelectionState } from '@tanstack/react-table';

import {
  isBeforeCutoff,
  isDepartureAfterArrival,
} from '@/components/blocks/berth-plan/berth-plan.utils';
import { dateTimeLocalToISOString } from '@/lib/utils';
import {
  EditShipVisitSchema,
  ShipVisit,
} from '@/routes/(app)/(scenarios)/(create)/types';

/**
 * The table sets `getRowId: (row) => row.id`, so the selection keys are ship
 * visit ids.
 */
export const getSelectedVisitIds = (
  rowSelection: RowSelectionState
): Array<string> =>
  Object.entries(rowSelection)
    .filter(([, isSelected]) => isSelected)
    .map(([visitId]) => visitId);

/**
 * Only the fields the planner changed (compared with the form's starting
 * values), so untouched values (an unknown `null` move count, say) are never
 * rewritten. Emptied move counts become `null`.
 */
export const toVisitPatch = (
  values: EditShipVisitSchema,
  initial: EditShipVisitSchema
): Partial<ShipVisit> => {
  const changed = (key: keyof EditShipVisitSchema) =>
    (values[key] ?? null) !== (initial[key] ?? null);
  return {
    ...(changed('arrival_time') && {
      arrival_time: dateTimeLocalToISOString(values.arrival_time),
    }),
    ...(changed('departure_time') && {
      departure_time: dateTimeLocalToISOString(values.departure_time),
    }),
    ...(changed('bollard_fore') && {
      bollard_fore: values.bollard_fore ?? null,
    }),
    ...(changed('bollard_aft') && { bollard_aft: values.bollard_aft ?? null }),
    ...(changed('n_moves_load') && {
      n_moves_load: values.n_moves_load ?? null,
    }),
    ...(changed('n_moves_discharge') && {
      n_moves_discharge: values.n_moves_discharge ?? null,
    }),
  };
};

export type CellEditError = 'arrivalBeforeCutoff' | 'departureAfterArrival';

/** Why a table edit to `columnId` is rejected, or `null` when it is valid. */
export const getCellEditError = (
  visit: ShipVisit,
  columnId: keyof ShipVisit,
  value: unknown,
  cutoffMs?: number
): CellEditError | null => {
  if (
    columnId === 'arrival_time' &&
    typeof value === 'string' &&
    isBeforeCutoff(value, cutoffMs)
  ) {
    return 'arrivalBeforeCutoff';
  }
  if (columnId !== 'arrival_time' && columnId !== 'departure_time') return null;
  const arrival = columnId === 'arrival_time' ? value : visit.arrival_time;
  const departure =
    columnId === 'departure_time' ? value : visit.departure_time;
  return typeof arrival === 'string' &&
    typeof departure === 'string' &&
    !isDepartureAfterArrival(arrival, departure)
    ? 'departureAfterArrival'
    : null;
};
